import { useState } from "react";
import { Link } from "react-router-dom";
import NetworkGraph from "../../components/NetworkGraph";
import Reveal from "../../components/Reveal";
import {
  IconMonitor, IconShield, IconDownload, IconAlertTriangle,
  IconWindows, IconApple, IconTerminal, IconSmartphone,
} from "../../components/Icons";

const ACCENT = "#00C2A0";

const FAQS = [
  {
    question: "Precisa instalar o RustDesk no computador?",
    answer:
      "Não necessariamente. A versão que disponibilizamos pode ser executada diretamente sem instalação — basta baixar e abrir o arquivo. Para uso recorrente, recomendamos instalar para facilitar os atendimentos futuros.",
  },
  {
    question: "O técnico pode acessar meu computador sem eu saber?",
    answer:
      "Não. Para cada sessão você precisar abrir o RustDesk e fornecer o ID e a senha temporária ao técnico. Sem esses dados, não é possível conectar. Além disso, você vê toda a atividade na tela em tempo real e pode encerrar a conexão quando quiser.",
  },
  {
    question: "O RustDesk sepolTECH é diferente do RustDesk oficial?",
    answer:
      "É a mesma tecnologia open source do RustDesk, com configurações pré-definidas da sepolTECH: nosso servidor de retransmissão, identidade visual e parametrização. Isso garante que a conexão seja sempre roteada pela infraestrutura da sepolTECH.",
  },
  {
    question: "Funciona em empresa com firewall corporativo?",
    answer:
      "Na maioria dos casos sim. O RustDesk usa a porta 443 (HTTPS) para conexão, que geralmente está aberta em redes corporativas. Em casos específicos de firewall muito restritivo, nosso suporte pode orientar a configuração necessária.",
  },
  {
    question: "Preciso pagar para usar?",
    answer:
      "Não. O acesso remoto via RustDesk sepolTECH é gratuito para todos os clientes sepolTECH, incluído em qualquer contrato de suporte ou gestão de TI.",
  },
];

const STEPS = [
  {
    title: "Baixe e abra o RustDesk",
    desc: "Clique no botão de download abaixo e execute o programa — não precisa instalar, basta abrir.",
  },
  {
    title: "Informe seu ID e senha ao técnico",
    desc: "O programa mostrará um ID e senha temporária. Passe esses dados ao técnico da sepolTECH pelo telefone ou chamado.",
  },
  {
    title: "Pronto — estamos conectados",
    desc: "Você acompanha tudo o que o técnico faz na tela em tempo real. A conexão é encerrada assim que o atendimento terminar.",
  },
];

const SECURITY_ITEMS = [
  {
    title: "Conexão criptografada",
    desc: "Comunicação protegida de ponta a ponta",
  },
  {
    title: "Senha única por sessão",
    desc: "Nova senha a cada abertura do programa",
  },
  {
    title: "Você vê tudo",
    desc: "Acompanha cada ação do técnico na sua tela",
  },
  {
    title: "Encerramento imediato",
    desc: "Você pode fechar a conexão a qualquer momento",
  },
  {
    title: "Servidor sepolTECH",
    desc: "Acesso roteado por infraestrutura própria da sepolTECH",
  },
];

const OS_DOWNLOADS = [
  { label: "macOS", Icon: IconApple, to: "https://github.com/rustdesk/rustdesk/releases/download/1.4.9/rustdesk-1.4.9-x86_64.dmg" },
  { label: "Linux", Icon: IconTerminal, to: "https://github.com/rustdesk/rustdesk/releases/download/1.4.9/rustdesk-1.4.9-0-x86_64.pkg.tar.zst" },
  { label: "Android", Icon: IconSmartphone, to: "https://github.com/rustdesk/rustdesk/releases/download/1.4.9/rustdesk-1.4.9-universal-signed.apk" },
  { label: "iOS", Icon: IconSmartphone, to: "https://apps.apple.com/us/app/rustdesk-remote-desktop/id1581225015" },
];

export default function AcessoRemoto() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  return (
    <>
      {/* ═══════════════ ABERTURA ═══════════════ */}
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="breadcrumb animate-up">
            <Link to="/">Início</Link>
            <span>/</span>
            <Link to="/servicos/servicos">Serviços</Link>
            <span>/</span>
            <span>Acesso Remoto</span>
          </div>
          <div className="tag animate-up animate-up--d1" style={{ color: ACCENT, background: "rgba(0,194,160,.12)", borderColor: `${ACCENT}40` }}>
            <IconMonitor style={{ width: "13px", height: "13px" }} />
            Suporte Remoto · RustDesk
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Acesso Remoto
            <br />
            <span style={{ color: ACCENT }}>sepolTECH</span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Permita que a equipe sepolTECH acesse seu computador remotamente
            para suporte técnico rápido e seguro — sem precisar de visita
            presencial.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="https://github.com/rustdesk/rustdesk/releases/tag/1.4.9" target="_blank" className="btn btn-primary">
              Baixar agora →
            </Link>
            <Link to="/contato" className="btn btn-outline">
              Falar com suporte
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════ COMO FUNCIONA ═══════════════ */}
      <section className="section">
        <div className="container">
          <div className="grid-2col">
            <Reveal>
              <div className="tag" style={{ color: ACCENT, background: "rgba(0,194,160,.1)", borderColor: "rgba(0,194,160,.25)" }}>
                Como funciona
              </div>
              <h2 className="section-title">
                Suporte remoto em
                <br />
                <span style={{ color: ACCENT }}>3 passos simples</span>
              </h2>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  marginTop: "32px",
                }}
              >
                {STEPS.map((step, index) => (
                  <div
                    key={step.title}
                    style={{ display: "flex", alignItems: "flex-start", gap: "20px" }}
                  >
                    <div className="step-num">{index + 1}</div>
                    <div>
                      <strong
                        style={{
                          fontSize: "17px",
                          display: "block",
                          marginBottom: "6px",
                          color: "var(--text-primary)",
                        }}
                      >
                        {step.title}
                      </strong>
                      <p
                        style={{
                          fontSize: "15px",
                          color: "var(--text-secondary)",
                          lineHeight: "1.6",
                        }}
                      >
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="card" style={{ "--accent": ACCENT, padding: "32px" }}>
                <h3
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "18px",
                    fontWeight: 700,
                    marginBottom: "20px",
                    color: ACCENT,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <IconShield style={{ width: "20px", height: "20px" }} />
                  Por que é seguro?
                </h3>
                <div className="feature-list">
                  {SECURITY_ITEMS.map((item) => (
                    <div className="feature-item" key={item.title}>
                      <div
                        className="feature-item__icon"
                        style={{ background: "rgba(0,194,160,.15)", color: ACCENT }}
                      >
                        ✓
                      </div>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>{item.title}</strong> —{" "}
                        {item.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--bg-border)",
                  borderRadius: "var(--radius-lg)",
                  padding: "20px 24px",
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <span
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "rgba(245,158,11,.15)",
                    color: "var(--warning)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconAlertTriangle style={{ width: "18px", height: "18px" }} />
                </span>
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--text-muted)",
                    lineHeight: "1.5",
                  }}
                >
                  <strong style={{ color: "var(--text-primary)" }}>Atenção:</strong>{" "}
                  Nunca forneça seu ID e senha para pessoas não identificadas. A
                  sepolTECH solicita acesso remoto apenas por chamado aberto e
                  confirmado.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════ DOWNLOAD ═══════════════ */}
      <section className="section section--alt" id="download">
        <div className="container">
          <Reveal
            className="download-card"
            style={{
              maxWidth: "720px",
              margin: "0 auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div
              style={{
                width: "72px",
                height: "72px",
                borderRadius: "var(--radius-lg)",
                background: "rgba(0,194,160,.12)",
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <IconDownload style={{ width: "32px", height: "32px" }} />
            </div>
            <div
              className="tag"
              style={{
                color: ACCENT,
                background: "rgba(0,194,160,.1)",
                borderColor: "rgba(0,194,160,.25)",
                marginBottom: "20px",
              }}
            >
              Download gratuito
            </div>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontSize: "32px",
                fontWeight: 700,
                letterSpacing: "-.02em",
                marginBottom: "12px",
                color: "var(--text-primary)",
              }}
            >
              RustDesk sepolTECH
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "var(--text-secondary)",
                marginBottom: "36px",
                lineHeight: "1.6",
                maxWidth: "480px",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              Versão personalizada com configurações da sepolTECH
              pré-instaladas. Clique no seu sistema operacional para baixar.
            </p>

            {/* Botão principal Windows */}
            <div style={{ marginBottom: "20px" }}>
              <Link to="https://github.com/rustdesk/rustdesk/releases/download/1.4.9/rustdesk-1.4.9-x86_64.exe" target="_blank" className="download-btn">
                <span className="download-btn__icon">
                  <IconWindows style={{ width: "18px", height: "18px" }} />
                </span>
                Baixar para Windows
              </Link>
            </div>

            {/* Outros sistemas */}
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "16px" }}>
              Outros sistemas operacionais:
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {OS_DOWNLOADS.map((os) => (
                <Link to={os.to} className="os-btn" key={os.label} target="_blank">
                  <os.Icon style={{ width: "15px", height: "15px" }} />
                  {os.label}
                </Link>
              ))}
            </div>

            <div
              style={{
                marginTop: "32px",
                paddingTop: "24px",
                borderTop: "1px solid var(--bg-border)",
              }}
            >
              <p style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                Versão baseada em{" "}
                <a
                  href="https://rustdesk.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--brand-blue)" }}
                >
                  RustDesk
                </a>{" "}
                — software open source de acesso remoto. Servidor hospedado
                pela sepolTECH. Grátis para clientes sepolTECH.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="section">
        <div className="container" style={{ maxWidth: "760px" }}>
          <Reveal className="text-center" style={{ marginBottom: "40px" }}>
            <div
              className="tag"
              style={{
                color: ACCENT,
                background: "rgba(0,194,160,.1)",
                borderColor: "rgba(0,194,160,.25)",
              }}
            >
              FAQ
            </div>
            <h2 className="section-title">
              Dúvidas sobre <span style={{ color: ACCENT }}>acesso remoto</span>
            </h2>
          </Reveal>

          {FAQS.map((faq, index) => (
            <Reveal
              className={`faq-item${openFaq === index ? " open" : ""}`}
              delay={index * 0.04}
              key={faq.question}
            >
              <button
                className="faq-btn"
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className="faq-btn__icon">+</span>
              </button>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section className="section section--alt">
        <div className="container">
          <Reveal className="cta-banner">
            <h2 className="cta-banner__title">Precisa de suporte agora?</h2>
            <p className="cta-banner__sub">
              Baixe o RustDesk sepolTECH e abra um chamado. Nossa equipe se
              conecta em minutos.
            </p>
            <div className="cta-banner__actions">
              <a
                href="https://github.com/rustdesk/rustdesk/releases/tag/1.4.9"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Baixar RustDesk →
              </a>
              <Link to="/contato" className="btn btn-outline">
                Abrir chamado
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
